import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import type { UiValidationIssue } from '../../models/ui-error.models';
import { AuthSessionService } from '../../services/auth-session.service';
import { ToastService } from '../../services/toast.service';
import { normalizeApiError } from '../../utils/error-normalizer';

interface SignupFieldErrors {
  name: string;
  email: string;
  password: string;
  otp: string;
}

const EMPTY_FIELD_ERRORS: SignupFieldErrors = {
  name: '',
  email: '',
  password: '',
  otp: '',
};

@Component({
  selector: 'app-signup',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
})
export class SignupComponent {
  step: 'details' | 'otp' = 'details';
  name = '';
  email = '';
  password = '';
  otp = '';
  emailMasked = '';
  otpExpiresAt = '';
  resendAvailableAt = '';
  remainingResends: number | null = null;
  loading = false;
  errorMessage = '';
  fieldErrors: SignupFieldErrors = { ...EMPTY_FIELD_ERRORS };

  private readonly authSession = inject(AuthSessionService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);

  submit(): void {
    if (this.step === 'otp') {
      this.verifyOtp();
      return;
    }

    if (this.loading) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.clearFieldErrors();

    this.authSession
      .signup({
        name: this.name.trim(),
        email: this.email.trim().toLowerCase(),
        password: this.password,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.step = 'otp';
          this.emailMasked = response.emailMasked;
          this.otpExpiresAt = response.otpExpiresAt;
          this.resendAvailableAt = response.resendAvailableAt;
          this.remainingResends = 5;
          this.otp = '';
          this.toast.success('Verification code sent to your email.');
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, { fallbackMessage: 'Signup failed' });
          const hasFieldErrors = this.applyFieldIssues(normalized.issues ?? []);
          this.errorMessage = hasFieldErrors ? '' : normalized.message;
          if (this.errorMessage) {
            this.toast.error(this.errorMessage);
          } else {
            this.toast.error('Please fix the errors in the form.');
          }
          this.cdr.markForCheck();
        },
      });
  }

  verifyOtp(): void {
    if (this.loading || this.step !== 'otp') {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.clearFieldErrors();

    this.authSession
      .verifySignupOtp({
        email: this.email.trim().toLowerCase(),
        otp: this.otp.trim(),
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.toast.success('Account created and verified successfully!');
          const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/dashboard';
          void this.router.navigateByUrl(returnUrl);
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, { fallbackMessage: 'OTP verification failed' });
          const hasFieldErrors = this.applyFieldIssues(normalized.issues ?? []);
          this.errorMessage = hasFieldErrors ? '' : normalized.message;
          if (this.errorMessage) {
            this.toast.error(this.errorMessage);
          } else {
            this.toast.error('Invalid verification code.');
          }
        },
      });
  }

  resendOtp(): void {
    if (this.loading || this.step !== 'otp') {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.authSession
      .resendSignupOtp({ email: this.email.trim().toLowerCase() })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.otpExpiresAt = response.otpExpiresAt;
          this.resendAvailableAt = response.resendAvailableAt;
          this.remainingResends = response.remainingResends;
          this.errorMessage = '';
          this.toast.success('New verification code sent.');
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, { fallbackMessage: 'Failed to resend OTP' });
          this.errorMessage = normalized.message;
          this.toast.error(this.errorMessage);
        },
      });
  }


  private clearFieldErrors(): void {
    this.fieldErrors = { ...EMPTY_FIELD_ERRORS };
  }

  private applyFieldIssues(issues: UiValidationIssue[]): boolean {
    this.clearFieldErrors();
    let hasFieldErrors = false;

    for (const issue of issues) {
      const field = issue.path?.[0];
      if (!field || issue.message.trim().length === 0) {
        continue;
      }

      if (field === 'name' || field === 'email' || field === 'password' || field === 'otp') {
        this.fieldErrors[field] = issue.message;
        hasFieldErrors = true;
      }
    }

    return hasFieldErrors;
  }
}
